import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2 } from "lucide-react";

const STAGES = ["12th Pass", "Currently in College", "Graduated", "Working Professional"];

const initialForm = { name: "", phone: "", email: "", stage: "" };

const validate = (form) => {
  const errors = {};
  if (!form.name.trim() || form.name.trim().length < 2) errors.name = "Please enter your full name";
  const phone = form.phone.replace(/[\s\-()]/g, "");
  if (!/^(\+?91)?[6-9]\d{9}$/.test(phone)) errors.phone = "Enter a valid 10-digit mobile number";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) errors.email = "Enter a valid email address";
  if (!form.stage) errors.stage = "Select your current stage";
  return errors;
};

const Field = ({ label, error, children, testId }) => (
  <div data-testid={testId}>
    <label className="block text-sm font-medium text-white mb-1.5">{label}</label>
    {children}
    {error && <p className="mt-1.5 text-xs" style={{ color: "var(--error)" }} data-testid={`${testId}-error`}>{error}</p>}
  </div>
);

export const SeminarModal = ({ open, onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setErrors({});
      setServerError("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    setServerError("");
    try {
      const res = await fetch("/api/seminar-registrations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("submit failed");
      onClose();
      navigate("/thank-you");
    } catch {
      setServerError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-shell fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="seminar-modal" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0" style={{ background: "rgba(13, 17, 23, 0.75)", backdropFilter: "blur(8px)" }} onClick={onClose} />
      <div
        className="modal-panel relative w-full sm:max-w-md sm:mx-4 rounded-t-2xl sm:rounded-2xl p-6 sm:p-8 overflow-y-auto"
        style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }}
      >
        <button
          data-testid="seminar-modal-close-button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:text-white"
          style={{ color: "var(--text-muted)" }}
        >
          <X size={20} />
        </button>

        <h3 className="font-display text-xl sm:text-2xl font-bold text-white" data-testid="seminar-modal-title">
          Reserve Your Free Seat
        </h3>
        <p className="mt-1.5 mb-6 text-sm" style={{ color: "var(--text-muted)" }}>
          Live seminar · Limited seats · Joining link sent to your email & phone.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Field label="Full Name" error={errors.name} testId="seminar-name-field">
            <input data-testid="seminar-name-input" type="text" value={form.name} onChange={set("name")} placeholder="e.g. Aarav Sharma"
              className={`lead-input w-full rounded-lg px-4 py-3 text-sm ${errors.name ? "input-error" : ""}`} />
          </Field>
          <Field label="Phone Number" error={errors.phone} testId="seminar-phone-field">
            <input data-testid="seminar-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="e.g. 98109 81090"
              className={`lead-input w-full rounded-lg px-4 py-3 text-sm ${errors.phone ? "input-error" : ""}`} />
          </Field>
          <Field label="Email" error={errors.email} testId="seminar-email-field">
            <input data-testid="seminar-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com"
              className={`lead-input w-full rounded-lg px-4 py-3 text-sm ${errors.email ? "input-error" : ""}`} />
          </Field>
          <Field label="Current Stage" error={errors.stage} testId="seminar-stage-field">
            <select data-testid="seminar-stage-select" value={form.stage} onChange={set("stage")}
              className={`lead-input w-full rounded-lg px-4 py-3 text-sm ${errors.stage ? "input-error" : ""}`}>
              <option value="" disabled>Select your current stage</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          {serverError && <p className="text-sm" style={{ color: "var(--error)" }} data-testid="seminar-server-error">{serverError}</p>}
          <button
            data-testid="seminar-submit-button"
            type="submit"
            disabled={submitting}
            className="cta-button w-full rounded-full py-3.5 text-sm sm:text-base font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting && <Loader2 size={18} className="animate-spin" />}
            Reserve My Free Seat
          </button>
          <p className="text-center text-xs" style={{ color: "var(--text-muted)" }}>
            Your details are 100% confidential. No spam, ever.
          </p>
        </form>
      </div>
    </div>
  );
};
